import { NPI_SEARCH_QUERY_INVALID_MESSAGE, isValidNpiSearchQuery } from '@/utils/freeTextValidation'
import * as Sentry from '@sentry/react'
import { useCallback, useState } from 'react'

export interface NPIAddress {
  address_1: string
  address_2?: string
  address_purpose: 'LOCATION' | 'MAILING'
  city: string
  state: string
  postal_code: string
  telephone_number?: string
  fax_number?: string
}

export interface NPITaxonomy {
  code: string
  desc: string
  primary: boolean
  state: string
  license?: string
}

export interface NPIResult {
  npiNumber: string
  firstName: string
  middleName?: string
  lastName: string
  /** NPPES `basic.credential`, e.g. "MD" / "DO". Maps to the member's medical title / roleTitle. */
  credential?: string
  organizationName?: string
  city: string
  state: string
  postalCode: string
  phoneNumber?: string
  faxNumber?: string
  addresses?: NPIAddress[]
  taxonomies?: NPITaxonomy[]
}

// Types for raw NPI API response
interface NPIApiAddress {
  address_1: string
  address_2?: string
  address_purpose: 'LOCATION' | 'MAILING'
  city: string
  state: string
  postal_code: string
  telephone_number?: string
  fax_number?: string
}

interface NpiSearchApiResponse {
  result_count?: number
  results?: NPIApiResult[]
}

interface NPIApiResult {
  number: string
  basic?: {
    first_name?: string
    middle_name?: string
    last_name?: string
    credential?: string
    organization_name?: string
  }
  addresses?: NPIApiAddress[]
  taxonomies?: Array<{
    code: string
    desc: string
    primary: boolean
    state: string
    license?: string
  }>
}

interface UseNPILookupResult {
  results: NPIResult[]
  isSearching: boolean
  error: string | null
  search: (query: string) => Promise<void>
  clearResults: () => void
}

/**
 * Hook for NPI Registry type-ahead search
 * Uses provider-api proxy endpoint to avoid CORS issues
 * Proxy calls NPPES NPI Registry API: https://npiregistry.cms.hhs.gov/api/
 * @returns {UseNPILookupResult} Search state and functions
 */
export const useNPILookup = (): UseNPILookupResult => {
  const [results, setResults] = useState<NPIResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const search = useCallback(async (query: string) => {
    const trimmedQuery = query.trim()
    if (!query || trimmedQuery.length < 2) {
      setResults([])
      return
    }

    // Client-side guard: an all-digit query is treated as an NPI number by
    // provider-api, which rejects anything over 10 digits with a 400. Catch it
    // here to give friendly feedback and avoid an unnecessary request/round-trip.
    if (/^\d+$/.test(trimmedQuery) && trimmedQuery.length > 10) {
      setResults([])
      setError('An NPI number can be at most 10 digits.')
      return
    }

    // Client-side guard: mirror the provider-api charset allowlist so disallowed
    // characters surface as friendly inline feedback instead of a generic 400.
    if (!isValidNpiSearchQuery(trimmedQuery)) {
      setResults([])
      setError(NPI_SEARCH_QUERY_INVALID_MESSAGE)
      return
    }

    setIsSearching(true)
    setError(null)

    try {
      await Sentry.startSpan(
        {
          op: 'http.client',
          name: 'NPI Registry Search',
        },
        async (span) => {
          span.setAttribute('search.query', trimmedQuery)

          // Use provider-api proxy endpoint to avoid CORS issues
          const apiUrl = `${import.meta.env['VITE_PROVIDER_API_URL']}/npi/search?query=${encodeURIComponent(trimmedQuery)}&limit=50`
          span.setAttribute('api.url', apiUrl)

          // Call provider-api proxy endpoint
          const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          })

          if (!response.ok) {
            // A 400 is an expected client-input rejection (e.g. invalid
            // characters or an over-length NPI). Surface it as friendly inline
            // feedback rather than throwing, so it is not reported to Sentry as
            // an error. Non-4xx responses still throw and are captured below.
            if (response.status === 400) {
              span.setAttribute('search.rejected', true)
              setResults([])
              setError('No matches found. Please refine your search.')
              return
            }
            throw new Error(`NPI search failed: ${response.status} ${response.statusText}`)
          }

          const npiApiResponse = (await response.json()) as NpiSearchApiResponse
          span.setAttribute('result.count', npiApiResponse.result_count || 0)

          // Handle empty results
          if (!npiApiResponse.results || npiApiResponse.results.length === 0) {
            setResults([])
            return
          }

          // Transform NPI API response to our format
          const transformedResults = npiApiResponse.results
            .map((result: NPIApiResult): NPIResult | null => {
              // Find LOCATION address, fallback to first address
              const locationAddress =
                result.addresses?.find((addr: NPIApiAddress) => addr.address_purpose === 'LOCATION') ||
                result.addresses?.[0]

              if (!locationAddress || !result.basic) {
                return null
              }

              const row: NPIResult = {
                npiNumber: result.number,
                firstName: result.basic.first_name || '',
                lastName: result.basic.last_name || '',
                city: locationAddress.city || '',
                state: locationAddress.state || '',
                postalCode: locationAddress.postal_code || '',
                addresses: (result.addresses || []) as NPIAddress[],
                taxonomies: (result.taxonomies || []) as NPITaxonomy[],
              }
              if (locationAddress.address_2) {
                row.organizationName = locationAddress.address_2
              }
              if (locationAddress.telephone_number) {
                row.phoneNumber = locationAddress.telephone_number
              }
              if (locationAddress.fax_number) {
                row.faxNumber = locationAddress.fax_number
              }
              if (result.basic.middle_name) {
                row.middleName = result.basic.middle_name
              }
              if (result.basic.credential) {
                row.credential = result.basic.credential
              }
              return row
            })
            .filter((result): result is NPIResult => result !== null)

          setResults(transformedResults)
        }
      )
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to search NPI Registry'
      setError(errorMessage)
      Sentry.captureException(err, {
        tags: {
          operation: 'npi.search',
        },
        extra: {
          query,
        },
      })
    } finally {
      setIsSearching(false)
    }
  }, [])

  const clearResults = useCallback(() => {
    setResults([])
    setError(null)
  }, [])

  return {
    results,
    isSearching,
    error,
    search,
    clearResults,
  }
}
